import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Menu, Share2 } from 'lucide-react';
import { apiFetch } from '../api';
import { toast } from 'react-hot-toast';

export default function SettingsPage({ user, navigate, onLogout, onUserUpdate, onShareSite }) {
  const [form, setForm] = useState({
    avatar: user?.avatar || '',
    lastName: user?.lastName || '',
    firstName: user?.firstName || '',
    middleName: user?.middleName || '',
    phone: user?.phone || '',
    notifyOnPlanReady: Boolean(user?.notifyOnPlanReady),
  });
  const fileInputRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'СоцПлан | Настройки';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 768px)');
    const handler = (event) => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setForm({
      avatar: user?.avatar || '',
      lastName: user?.lastName || '',
      firstName: user?.firstName || '',
      middleName: user?.middleName || '',
      phone: user?.phone || '',
      notifyOnPlanReady: Boolean(user?.notifyOnPlanReady),
    });
  }, [user]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const saveProfile = useCallback(
    async (values) => {
      const payload = {
        firstName: values.firstName?.trim() || null,
        lastName: values.lastName?.trim() || null,
        middleName: values.middleName?.trim() || null,
        phone: values.phone?.trim() || null,
        notifyOnPlanReady: values.notifyOnPlanReady,
      };
      try {
        const res = await apiFetch('/user', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) return null;
        const data = await res.json();
        localStorage.setItem('user', JSON.stringify(data));
        if (onUserUpdate) {
          onUserUpdate(data);
        }
        return data;
      } catch {
        return null;
      }
    },
    [onUserUpdate]
  );

  const handleSave = useCallback(async () => {
    const updated = await saveProfile(form);
    if (updated) {
      toast.success('Сохранено');
    } else {
      toast.error('Ошибка сохранения');
    }
  }, [form, saveProfile]);

  const handlePickAvatar = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiFetch('/user/avatar', { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm((f) => {
          const next = { ...f, avatar: data.avatar };
          void saveProfile(next);
          return next;
        });
      }
    } catch {
      // handled globally
    }
  };

  const sidebarItems = [
    { title: 'Ваши бизнес планы', onClick: () => navigate('account') },
    { title: 'Настройки', current: true },
  ];
  const hasMenuAction = (item) => Boolean(item.onClick);
  const handleMenuItem = (item, closeAfter = false) => {
    if (item.onClick) {
      item.onClick();
    }
    if (closeAfter) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky sticky-safe-top z-30 border-b bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Открыть меню"
              aria-expanded={mobileMenuOpen}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('home');
                }}
                className="font-medium text-slate-700 hover:text-slate-900"
              >
                Главная
              </button>
              <span className="text-slate-400">→</span>
              <span className="font-semibold text-slate-900">Личный кабинет</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {onShareSite && (
              <button
                type="button"
                onClick={onShareSite}
                aria-label="Поделиться сайтом"
                className="inline-flex items-center gap-2 px-2 py-2 text-xs md:text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                <Share2 className="h-4 w-4" />
                <span>Поделиться</span>
              </button>
            )}
            <button onClick={onLogout} className="text-sm text-slate-600 hover:text-slate-900">Выйти</button>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">Навигация</div>
          <div className="mt-2 space-y-1">
            {sidebarItems.map((item, index) => (
              <div key={index}>
                {item.current ? (
                  <div className="px-2 py-2 rounded-md bg-slate-100 text-sm font-medium text-slate-900">
                    {item.title}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleMenuItem(item, true)}
                    className="block w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                  >
                    {item.title}
                  </button>
                )}
              </div>
            ))}
          </div>
          {onShareSite && (
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                onShareSite();
              }}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Поделиться
            </button>
          )}
        </div>
      )}

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-5 md:grid-cols-12 md:gap-6 md:py-6">
        <aside className="hidden md:col-span-3 md:block">
          <div className="space-y-1 rounded-xl border bg-white p-3">
            {sidebarItems.map((item, index) => (
              item.current ? (
                <div key={index} className="rounded-md bg-slate-100 px-2 py-2 text-sm font-medium text-slate-900">
                  {item.title}
                </div>
              ) : (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleMenuItem(item)}
                  className="w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  {item.title}
                </button>
              )
            ))}
          </div>
        </aside>

        <main className="col-span-12 md:col-span-9 space-y-4">
          <div className="rounded-xl border bg-white p-5 space-y-4">
            <div className="flex flex-col items-center gap-4">
              <button onClick={handlePickAvatar} className="relative group">
                <img
                  src={form.avatar || '/user-placeholder.svg'}
                  alt="avatar"
                  className="w-24 h-24 rounded-full object-cover border"
                />
                <span className="absolute inset-0 rounded-full bg-black/25 opacity-0 group-hover:opacity-100 transition" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <div className="space-y-3">
              <input
                value={form.lastName}
                onChange={handleChange('lastName')}
                placeholder="Фамилия"
                className="w-full rounded-lg border px-3 py-2 text-[15px]"
              />
              <input
                value={form.firstName}
                onChange={handleChange('firstName')}
                placeholder="Имя"
                className="w-full rounded-lg border px-3 py-2 text-[15px]"
              />
              <input
                value={form.middleName}
                onChange={handleChange('middleName')}
                placeholder="Отчество"
                className="w-full rounded-lg border px-3 py-2 text-[15px]"
              />
              <input
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="Телефон"
                className="w-full rounded-lg border px-3 py-2 text-[15px]"
              />
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.notifyOnPlanReady)}
                  onChange={(e) => setForm({ ...form, notifyOnPlanReady: e.target.checked })}
                  className="mt-0.5 h-4 w-4"
                />
                <span>Отправлять уведомление о завершении генерации (не обязательно)</span>
              </label>
            </div>
            <button
              onClick={handleSave}
              className="mt-4 inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold hover:bg-blue-700"
            >
              Сохранить
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
